// src/components/PayoutRequest.jsx
// Full Payout Request component for stylists — with history, saved bank details, validation, and admin review flow

import React, { useState, useEffect } from 'react';
import {
  collection, addDoc, serverTimestamp, onSnapshot,
  query, where, orderBy, limit, doc, getDoc
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import toast from 'react-hot-toast';

const GOLD = '#B8860B';
const NAVY = '#001F3F';

const NIGERIAN_BANKS = [
  'Access Bank', 'GTBank', 'First Bank', 'UBA', 'Zenith Bank',
  'Ecobank', 'Fidelity Bank', 'FCMB', 'Polaris Bank', 'Sterling Bank',
  'Union Bank', 'Wema Bank', 'Opay', 'Palmpay', 'Kuda Bank',
  'Moniepoint', 'Carbon', 'VFD Bank', 'Stanbic IBTC', 'Keystone Bank',
  'Heritage Bank', 'Unity Bank', 'Suntrust Bank', 'Providus Bank',
  'Titan Trust Bank', 'Globus Bank', 'Signature Bank', 'Premium Trust Bank',
  'Jaiz Bank', 'Lotus Bank', 'Rephidim Microfinance Bank', 'Taj Bank',
  'Mint Bank', 'Nova Merchant Bank', 'Citi Bank Nigeria', 'Standard Chartered Bank'
];

const MIN_PAYOUT = 1000; // ₦1,000 minimum

/**
 * Professional Naira Formatter Utility
 */
const formatNaira = (amount) => {
  if (amount === undefined || amount === null) return "₦0.00";
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
  }).format(amount).replace('NGN', '₦');
};

export default function PayoutRequest({ walletBalance, stylistName }) {
  const [form, setForm] = useState({
    amount: '',
    bankName: '',
    accountNumber: '',
    accountName: ''
  });
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [savedBank, setSavedBank] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});

  const user = auth.currentUser;

  // Load saved bank details + payout history
  useEffect(() => {
    if (!user) return;

    // Load saved bank info from barbers collection
    const loadBank = async () => {
      const barberSnap = await getDoc(doc(db, 'barbers', user.uid));
      if (barberSnap.exists()) {
        const data = barberSnap.data();
        if (data.bankName && data.accountNumber) {
          setSavedBank({
            bankName: data.bankName,
            accountNumber: data.accountNumber,
            accountName: data.accountName || stylistName || '',
          });
          setForm(prev => ({
            ...prev,
            bankName: data.bankName,
            accountNumber: data.accountNumber,
            accountName: data.accountName || stylistName || '',
          }));
        }
      }
    };
    loadBank();

    // Listen to recent payout requests
    const q = query(
      collection(db, 'payouts'),
      where('stylistId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(5)
    );

    const unsub = onSnapshot(q, (snap) => {
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => console.error('Payouts listener error:', err));

    return () => unsub();
  }, [user, stylistName]);

  // Form validation
  const validate = () => {
    const errs = {};
    const amount = parseFloat(form.amount);

    if (!amount || isNaN(amount)) errs.amount = 'Please enter a valid amount.';
    else if (amount < MIN_PAYOUT) errs.amount = `Minimum withdrawal is ${formatNaira(MIN_PAYOUT)}.`;
    else if (amount > walletBalance) errs.amount = 'Amount exceeds your available balance.';

    if (!form.bankName.trim()) errs.bankName = 'Please select a bank.';
    if (!/^\d{10}$/.test(form.accountNumber)) errs.accountNumber = 'Account number must be exactly 10 digits.';
    if (!form.accountName.trim()) errs.accountName = 'Please enter the account holder name.';

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Submit payout request
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!user) {
      toast.error('Please log in.');
      return;
    }

    const amount = parseFloat(form.amount);
    if (!window.confirm(`Request withdrawal of ${formatNaira(amount)} to ${form.bankName} — ${form.accountNumber}?`)) return;

    setLoading(true);
    try {
      await addDoc(collection(db, 'payouts'), {
        stylistId: user.uid,
        stylistName: stylistName || user.displayName || 'Stylist',
        stylistEmail: user.email,
        amount,
        bankName: form.bankName.trim(),
        accountNumber: form.accountNumber.trim(),
        accountName: form.accountName.trim(),
        status: 'pending',
        gateway: 'monnify_transfer',
        createdAt: serverTimestamp(),
        balanceAtRequest: walletBalance,
      });

      toast.success('Withdrawal request submitted! Admin will process within 24 hours.');
      setForm({ amount: '', bankName: '', accountNumber: '', accountName: '' });
      setShowForm(false);
    } catch (err) {
      console.error('Payout request error:', err);
      toast.error('Request failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasPendingRequest = history.some(h => h.status === 'pending');

  const getStatusColor = (status) => {
    if (status === 'approved' || status === 'completed') return '#2ecc71';
    if (status === 'rejected' || status === 'failed') return '#ff4d4d';
    return GOLD; // pending
  };

  const getStatusLabel = (status) => {
    const map = {
      pending: 'PENDING REVIEW',
      approved: 'APPROVED',
      completed: 'PAID OUT',
      rejected: 'REJECTED',
      failed: 'FAILED',
    };
    return map[status] || status?.toUpperCase() || 'UNKNOWN';
  };

  return (
    <div style={cardStyle}>
      {/* Header */}
      <div style={cardHeader}>
        <div>
          <h3 style={{ color: GOLD, margin: 0, fontSize: '0.9rem', fontWeight: '900' }}>
            WITHDRAW FUNDS
          </h3>
          <p style={{ fontSize: '0.65rem', opacity: 0.5, margin: '4px 0 0' }}>
            Available: {formatNaira(walletBalance)}
          </p>
        </div>

        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            disabled={walletBalance < MIN_PAYOUT || hasPendingRequest}
            style={{
              ...withdrawBtn,
              opacity: (walletBalance < MIN_PAYOUT || hasPendingRequest) ? 0.5 : 1,
              cursor: (walletBalance < MIN_PAYOUT || hasPendingRequest) ? 'not-allowed' : 'pointer',
            }}
          >
            + REQUEST PAYOUT
          </button>
        )}
      </div>

      {/* Pending warning */}
      {hasPendingRequest && !showForm && (
        <div style={alertBox}>
          <p style={{ margin: 0, fontSize: '0.7rem', color: GOLD }}>
            ⏳ A payout request is currently pending admin review. You cannot submit another until it is processed.
          </p>
        </div>
      )}

      {/* Low balance warning */}
      {walletBalance < MIN_PAYOUT && !hasPendingRequest && (
        <div style={alertBox}>
          <p style={{ margin: 0, fontSize: '0.7rem', opacity: 0.7 }}>
            Minimum withdrawal is {formatNaira(MIN_PAYOUT)}. Your current balance is {formatNaira(walletBalance)}.
          </p>
        </div>
      )}

      {/* Request Form */}
      {showForm && (
        <form onSubmit={handleSubmit} style={formStyle}>
          {/* Amount */}
          <div style={fieldGroup}>
            <label style={fieldLabel}>AMOUNT (₦)</label>
            <input
              type="number"
              min={MIN_PAYOUT}
              max={walletBalance}
              placeholder={`Min ${formatNaira(MIN_PAYOUT)}`}
              value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              style={{ ...fieldInput, borderColor: errors.amount ? '#ff4d4d' : '#333' }}
            />
            {errors.amount && <p style={errText}>{errors.amount}</p>}

            {/* Quick amounts */}
            <div style={quickAmounts}>
              {[walletBalance * 0.25, walletBalance * 0.5, walletBalance].map((amt, i) => {
                const label = ['25%', '50%', 'ALL'];
                const rounded = Math.floor(amt);
                if (rounded < MIN_PAYOUT) return null;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm({ ...form, amount: String(rounded) })}
                    style={quickBtn}
                  >
                    {label[i]} ({formatNaira(rounded)})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bank Name */}
          <div style={fieldGroup}>
            <label style={fieldLabel}>BANK NAME</label>
            <select
              value={form.bankName}
              onChange={e => setForm({ ...form, bankName: e.target.value })}
              style={{ ...fieldInput, borderColor: errors.bankName ? '#ff4d4d' : '#333', cursor: 'pointer' }}
            >
              <option value="">Select your bank...</option>
              {NIGERIAN_BANKS.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
            {errors.bankName && <p style={errText}>{errors.bankName}</p>}
          </div>

          {/* Account Number */}
          <div style={fieldGroup}>
            <label style={fieldLabel}>ACCOUNT NUMBER (10 DIGITS)</label>
            <input
              type="text"
              inputMode="numeric"
              maxLength={10}
              placeholder="0123456789"
              value={form.accountNumber}
              onChange={e => setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              style={{ ...fieldInput, borderColor: errors.accountNumber ? '#ff4d4d' : '#333' }}
            />
            {errors.accountNumber && <p style={errText}>{errors.accountNumber}</p>}
          </div>

          {/* Account Name */}
          <div style={fieldGroup}>
            <label style={fieldLabel}>ACCOUNT HOLDER NAME</label>
            <input
              type="text"
              placeholder="As it appears on the account"
              value={form.accountName}
              onChange={e => setForm({ ...form, accountName: e.target.value })}
              style={{ ...fieldInput, borderColor: errors.accountName ? '#ff4d4d' : '#333' }}
            />
            {errors.accountName && <p style={errText}>{errors.accountName}</p>}
          </div>

          {/* Info note */}
          <div style={infoNote}>
            <p style={{ margin: 0, fontSize: '0.65rem', opacity: 0.7 }}>
              ℹ️ Payouts are processed manually by the Esdras admin team via Monnify bank transfer.
              Allow up to 24 hours on business days. A 10% platform commission has already been deducted.
            </p>
          </div>

          {/* Submit & Cancel */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <button
              type="submit"
              disabled={loading}
              style={{ ...submitBtn, opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
            </button>
            <button
              type="button"
              onClick={() => { setShowForm(false); setErrors({}); }}
              style={cancelBtn}
            >
              CANCEL
            </button>
          </div>
        </form>
      )}

      {/* Payout History */}
      {history.length > 0 && (
        <div style={{ marginTop: '25px' }}>
          <p style={historyTitle}>RECENT REQUESTS</p>
          {history.map(item => (
            <div key={item.id} style={historyRow}>
              <div>
                <p style={{ margin: 0, fontWeight: '700', fontSize: '0.85rem' }}>
                  {formatNaira(item.amount)}
                </p>
                <p style={{ margin: '3px 0 0', fontSize: '0.7rem', opacity: 0.6 }}>
                  {item.bankName} • {item.accountNumber}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: '0.65rem', opacity: 0.5 }}>
                  {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString('en-NG') : 'Recent'}
                </p>
              </div>
              <span style={{ ...statusBadge, color: getStatusColor(item.status), borderColor: getStatusColor(item.status) }}>
                {getStatusLabel(item.status)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'rgba(255,255,255,0.03)',
  padding: '1.8rem',
  borderRadius: '20px',
  border: '1px solid rgba(255,255,255,0.1)',
  fontFamily: 'Montserrat, sans-serif'
};

const cardHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '16px'
};

const withdrawBtn = {
  background: GOLD,
  color: 'black',
  border: 'none',
  padding: '10px 18px',
  borderRadius: '50px',
  fontWeight: '900',
  fontSize: '0.72rem',
  letterSpacing: '1px'
};

const alertBox = {
  background: 'rgba(184,134,11,0.08)',
  border: '1px solid rgba(184,134,11,0.2)',
  borderRadius: '10px',
  padding: '12px',
  marginBottom: '14px'
};

const formStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  marginTop: '16px'
};

const fieldGroup = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px'
};

const fieldLabel = {
  fontSize: '0.62rem',
  color: GOLD,
  fontWeight: '900',
  letterSpacing: '1.5px'
};

const fieldInput = {
  background: 'rgba(0,0,0,0.35)',
  border: '1px solid #333',
  borderRadius: '10px',
  color: 'white',
  padding: '13px',
  fontSize: '0.9rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
};

const errText = {
  color: '#ff4d4d',
  fontSize: '0.65rem',
  margin: '2px 0 0'
};

const quickAmounts = {
  display: 'flex',
  gap: '8px',
  flexWrap: 'wrap',
  marginTop: '8px'
};

const quickBtn = {
  background: 'rgba(184,134,11,0.1)',
  border: `1px solid rgba(184,134,11,0.3)`,
  color: GOLD,
  padding: '6px 12px',
  borderRadius: '20px',
  fontSize: '0.65rem',
  fontWeight: '700',
  cursor: 'pointer'
};

const infoNote = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  padding: '12px',
  fontSize: '0.68rem',
  opacity: 0.8
};

const submitBtn = {
  background: GOLD,
  color: 'black',
  border: 'none',
  padding: '15px',
  borderRadius: '12px',
  fontWeight: '900',
  fontSize: '0.82rem',
  cursor: 'pointer',
  flex: 2,
  letterSpacing: '1px'
};

const cancelBtn = {
  background: 'rgba(255,255,255,0.07)',
  color: 'white',
  border: '1px solid rgba(255,255,255,0.12)',
  padding: '15px',
  borderRadius: '12px',
  cursor: 'pointer',
  fontSize: '0.8rem',
  flex: 1
};

const historyTitle = {
  fontSize: '0.62rem',
  color: GOLD,
  fontWeight: '900',
  letterSpacing: '2px',
  marginBottom: '10px'
};

const historyRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 0',
  borderBottom: '1px solid rgba(255,255,255,0.05)'
};

const statusBadge = {
  fontSize: '0.58rem',
  fontWeight: '900',
  padding: '4px 10px',
  borderRadius: '6px',
  border: '1px solid',
  letterSpacing: '0.5px'
};
